import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
const holdIcon = "/quarantine/hold.svg";
const closeIcon = "/quarantine/close.svg";
const scanIcon = "/quarantine/scan.svg";

export type MovementBlock = {
  code: "legal_hold" | "foreign_client" | "not_on_hold";
  message: string;
  inventoryId: number;
  serialNumber?: string | null;
  clientName?: string | null;
  orderNumber?: string | null;
  holdStatus?: string | null;
  expiration?: string | null;
  destinationCode?: string | null;
  destinationClientName?: string | null;
  nextAction: "scan_location" | "contact_supervisor";
};

export function movementBlockFromError(error: unknown): MovementBlock | undefined {
  return (error as { response?: { data?: { errors?: { blocker?: MovementBlock } } } }).response?.data?.errors?.blocker;
}

export function MovementBlocker({
  block: currentBlock,
  onClose,
  onExited,
}: {
  block: MovementBlock | null;
  onClose: () => void;
  onExited?: () => void;
}) {
  const [lastBlock, setLastBlock] = useState(currentBlock);
  useEffect(() => {
    if (currentBlock) setLastBlock(currentBlock);
  }, [currentBlock]);
  // Keep the refusal visible until MUI finishes the exit transition.
  const block = currentBlock || lastBlock;
  const held = block?.holdStatus === "active" || block?.holdStatus === "return_requested";
  const title =
    block?.code === "not_on_hold"
      ? "QRTN shelves are for legal hold devices only."
      : block?.code === "foreign_client"
        ? "This location belongs to another client."
        : "This device is on legal hold and can’t be moved here.";
  const unknownClient = !block?.clientName || block?.nextAction === "contact_supervisor";
  const instruction = unknownClient
    ? "Ask an intake supervisor to verify this device’s hold details."
    : held
      ? `Use an active quarantine location for ${block?.clientName}.`
      : "Scan a non-quarantine location allowed for this device.";
  const expiration = block?.expiration ? new Date(block.expiration) : null;
  const date =
    expiration && !Number.isNaN(expiration.getTime())
      ? expiration.toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZoneName: "short",
        })
      : "Unknown — ask an intake supervisor";
  const details = [
    ["Device", block?.serialNumber || `Inventory ${block?.inventoryId}`],
    ["Client", block?.clientName || "Unknown"],
    ["Order", block?.orderNumber || "Not available"],
    [held ? "Hold expiration" : "Legal hold", held ? date : "Not on hold"],
  ];
  return (
    <Dialog
      open={Boolean(currentBlock)}
      onClose={onClose}
      aria-labelledby="movement-blocker-title"
      aria-describedby="movement-blocker-description"
      disableRestoreFocus
      TransitionProps={{ onExited }}
      PaperProps={{ role: "alertdialog", sx: { width: 520, maxWidth: "calc(100% - 32px)" } }}
    >
      <DialogContent sx={{ p: "20px 24px 24px", display: "flex", flexDirection: "column", gap: "18px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              px: "12px",
              py: "4px",
              borderRadius: "99px",
              bgcolor: held ? "#fdecea" : "grey.200",
              color: held ? "error.main" : "text.primary",
            }}
          >
            {held && <img src={holdIcon} alt="" />}
            <Typography fontSize={13} fontWeight={600}>
              {held ? "Legal hold" : "Not on legal hold"}
            </Typography>
          </Box>
          <IconButton aria-label="Close movement blocker" onClick={onClose}>
            <img src={closeIcon} alt="" />
          </IconButton>
        </Box>
        <Box>
          <Typography id="movement-blocker-title" component="h2" fontSize={21} fontWeight={600} lineHeight={1.35}>
            {title}
          </Typography>
          <Typography id="movement-blocker-description" sx={{ mt: 1 }} fontSize={15}>
            {block?.code === "foreign_client" && block.destinationClientName
              ? `${block.destinationCode || "This location"} belongs to ${block.destinationClientName}. This device’s client is ${block.clientName || "unknown"}.`
              : block?.message}
          </Typography>
        </Box>
        <Box
          component="dl"
          sx={{
            m: 0,
            p: "14px 16px",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "12px 24px",
            overflowWrap: "anywhere",
          }}
        >
          {details.map(([label, value]) => (
            <Box key={label}>
              <Typography component="dt" fontSize={12} color="text.secondary" sx={{ textTransform: "uppercase" }}>
                {label}
              </Typography>
              <Typography component="dd" sx={{ m: 0 }} fontSize={15} fontWeight={600}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ p: "14px 16px", border: 1, borderColor: "primary.main", bgcolor: "primary.light", borderRadius: 1 }}>
          <Typography fontSize={12} color="primary.dark" fontWeight={600}>
            NEXT ACTION
          </Typography>
          <Typography fontSize={20} fontWeight={600}>
            {instruction}
          </Typography>
          {held && <Typography fontSize={13}>The hold remains active until the server releases it.</Typography>}
        </Box>
        <Button
          autoFocus
          fullWidth
          variant="contained"
          sx={{ minHeight: 48 }}
          onClick={onClose}
          startIcon={unknownClient ? undefined : <img src={scanIcon} alt="" />}
        >
          {unknownClient ? "Ask an intake supervisor" : "Scan a different location"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
